import { Controller } from "@hotwired/stimulus"
import { folderEntries, stillToSend, batches, formatBytes } from "../lib/remote_eod_plan"

// Remote End of Day (/djobs/:id/remote_eods). The photographer picks the
// laptop's photo.db and its school folder; this sends both to S3 through
// one-time upload URLs from Djobs::RemoteEodsController, so the browser never
// holds AWS keys. A rerun skips what already arrived. The server, not this
// code, decides the run is complete -- it lists S3 against our manifest.
//
// Step order matters: photo.db first, because the server learns the laptop's
// camera letter from it and the folder's destination depends on that letter.
const PRESIGN_BATCH = 100 // URLs live an hour; a batch must finish well inside that
const PARALLEL = 4
const ATTEMPTS = 3

export default class extends Controller {
  static targets = ["db", "folder", "start", "status", "bar", "progress", "summary", "missing", "folderNote"]
  static values = { url: String, scode: String }

  connect() {
    this.running = false
    this.warnOnLeave = (event) => {
      if (!this.running) return
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", this.warnOnLeave)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.warnOnLeave)
  }

  // Shown as soon as a folder is picked, so a wrong folder is caught before
  // anything is sent.
  folderChosen() {
    const entries = folderEntries(this.folderTarget.files, this.scodeValue)
    const bytes = entries.reduce((sum, { file }) => sum + file.size, 0)
    this.folderNoteTarget.textContent = entries.length
      ? `${entries.length} files, ${formatBytes(bytes)} in school folder ${this.scodeValue}.`
      : `No files for school ${this.scodeValue} in that folder. Choose C:\\Jobs20xx\\${this.scodeValue}.`
    this.folderNoteTarget.classList.toggle("text-danger", entries.length === 0)
  }

  async start() {
    const db = this.dbTarget.files[0]
    const entries = folderEntries(this.folderTarget.files, this.scodeValue)
    if (!db) return this.fail("Choose photo.db first.")
    if (entries.length === 0) return this.fail(`Choose the school folder (C:\\Jobs20xx\\${this.scodeValue}).`)

    this.running = true
    this.startTarget.disabled = true
    const wakeLock = await this.holdWake()
    try {
      const run = await this.request("POST", this.urlValue)
      this.say("Sending photo.db…")
      await this.put(run.db_upload_url, db, (sent) => this.showBytes(sent, db.size, "photo.db"))

      this.say("Reading photo.db…")
      const analysis = await this.request("POST", `${this.urlValue}/${run.id}/analyze`)
      this.showAnalysis(analysis)

      const { sizes } = await this.request("GET", `${this.urlValue}/${run.id}/existing`)
      const toSend = stillToSend(entries, sizes)
      await this.sendFolder(run.id, toSend, entries.length - toSend.length)

      this.say("Checking everything arrived…")
      const done = await this.request("POST", `${this.urlValue}/${run.id}/complete`, {
        files: entries.map(({ path, file }) => [path, file.size])
      })
      this.say(`Done. ${done.files_count} files (${formatBytes(done.bytes_total)}) are in S3 for ${analysis.job_with_camera}. The studio will pull them onto V:.`, "text-success")
    } catch (error) {
      this.fail(`${error.message} Nothing sent so far is lost; press Send again to finish.`)
    } finally {
      this.running = false
      this.startTarget.disabled = false
      wakeLock?.release?.()
    }
  }

  async sendFolder(runId, toSend, alreadyThere) {
    const total = toSend.reduce((sum, { file }) => sum + file.size, 0)
    let sent = 0
    let filesDone = 0
    const inFlight = new Map()
    const report = () => {
      const now = sent + [...inFlight.values()].reduce((a, b) => a + b, 0)
      this.showBytes(now, total, `${filesDone} of ${toSend.length} files` +
        (alreadyThere ? ` (${alreadyThere} already there)` : ""))
    }
    report()

    for (const batch of batches(toSend, PRESIGN_BATCH)) {
      const { urls } = await this.request("POST", `${this.urlValue}/${runId}/presign`, {
        paths: batch.map(({ path }) => path)
      })
      const queue = [...batch]
      const worker = async () => {
        while (queue.length) {
          const { path, file } = queue.shift()
          await this.put(urls[path], file, (bytes) => { inFlight.set(path, bytes); report() })
          inFlight.delete(path)
          sent += file.size
          filesDone += 1
          report()
        }
      }
      await Promise.all(Array.from({ length: PARALLEL }, worker))
    }
  }

  showAnalysis(analysis) {
    const lines = [
      `Camera ${analysis.camera}: ${analysis.poses_count} photos in photo.db for ${analysis.job_with_camera}.`,
      analysis.missing_count
        ? `${analysis.missing_count} of them have not reached the website yet (listed below).`
        : "All of them have reached the website."
    ]
    if (analysis.other_cameras.length) {
      lines.push(`This photo.db also has camera ${analysis.other_cameras.join(", ")} for this job; only ${analysis.camera} is sent.`)
    }
    this.summaryTarget.textContent = lines.join(" ")
    this.showList("Not on the website yet:", analysis.missing)
  }

  showList(heading, names) {
    this.missingTarget.textContent = [heading, ...names].join("\n")
    this.missingTarget.hidden = names.length === 0
  }

  // PUT a file to a presigned URL, with progress and retries. XHR rather than
  // fetch because fetch can't report upload progress.
  async put(url, file, onProgress) {
    for (let attempt = 1; ; attempt += 1) {
      try {
        return await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open("PUT", url)
          xhr.upload.onprogress = (event) => onProgress(event.loaded)
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`S3 refused ${file.name} (${xhr.status}).`)))
          xhr.onerror = () => reject(new Error(`Network error sending ${file.name}.`))
          xhr.send(file)
        })
      } catch (error) {
        if (attempt >= ATTEMPTS) throw error
        await new Promise((r) => setTimeout(r, 2000 * attempt))
      }
    }
  }

  async request(method, url, body = null) {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: body ? JSON.stringify(body) : null
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      const error = new Error(data.error || `The website answered ${response.status}.`)
      if (data.missing) this.showList("Not in S3 yet:", data.missing)
      throw error
    }
    return data
  }

  // Keep the screen (and so the laptop) awake through a long upload, where the
  // browser allows it.
  async holdWake() {
    try {
      return await navigator.wakeLock?.request("screen")
    } catch (_error) {
      return null
    }
  }

  showBytes(done, total, label) {
    const percent = total ? Math.min(100, Math.round((done / total) * 100)) : 100
    this.barTarget.style.width = `${percent}%`
    this.progressTarget.textContent = `${label}: ${formatBytes(done)} of ${formatBytes(total)} (${percent}%)`
  }

  say(message, cls = "") {
    this.statusTarget.textContent = message
    this.statusTarget.className = cls
  }

  fail(message) {
    this.say(message, "text-danger")
  }
}
